import {Stack} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import type * as React from 'react';
import {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {isJwtExpired} from '../../shared/jwt';
import SignIn from '../Pages/SignIn';

type Props = {
    children?: React.ReactNode;
};

export type JWTUser = {
    accessToken: string;
};

export const JWT_STORAGE_KEY = 'EnrollmentAdminJWT';

type AuthenticateUser = (username: string, password: string) => Promise<JWTUser | null>;

type JwtContextValue = {
    user: JWTUser;
    logout: () => void;
    authenticate: AuthenticateUser;
};

export const JwtContext = createContext<JwtContextValue | null>(null);

const authenticateRequest = async (username: string, password: string): Promise<JWTUser | null> => {
    const init: RequestInit = {
        headers: new Headers({'Content-Type': 'application/json'}),
        method: 'POST',
        body: JSON.stringify({username, password}),
    };
    const response = await fetch(
        `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/login/authenticate`,
        init,
    );

    if (response.status !== 200) {
        return null;
    }

    const data = (await response.json()) as {jwt: string};
    const user = {accessToken: data.jwt};

    localStorage.setItem(JWT_STORAGE_KEY, JSON.stringify(user));

    return user;
};

const JWTProvider: React.FC<Props> = ({children}: Props): React.ReactElement => {
    const [user, setUser] = useState<JWTUser | null>();
    useEffect(() => {
        const stored = localStorage.getItem(JWT_STORAGE_KEY);

        if (!stored) {
            setUser(null);
            return;
        }

        try {
            const parsed = JSON.parse(stored) as JWTUser;

            if (parsed?.accessToken && !isJwtExpired(parsed.accessToken)) {
                setUser(parsed);
                return;
            }
        } catch {
            console.error('discarding unreadable stored credentials');
        }

        localStorage.removeItem(JWT_STORAGE_KEY);
        setUser(null);
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(JWT_STORAGE_KEY);
        setUser(null);
    }, []);

    const authenticate = useCallback(async (username: string, password: string) => {
        const result = await authenticateRequest(username, password);

        if (result) {
            setUser(result);
        }

        return result;
    }, []);

    if (user === undefined) {
        return (
            <Stack
                sx={{
                    height: '100vh',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress sx={{mt: 1}} />
            </Stack>
        );
    }

    if (user === null) {
        return <SignIn authenticate={authenticate} />;
    }

    return (
        <JwtContext.Provider value={{user, logout, authenticate}}>{children}</JwtContext.Provider>
    );
};

export const useUser = (): JwtContextValue => {
    const ctx = useContext(JwtContext);

    if (!ctx) {
        throw new Error('useUser must be used within JWTProvider');
    }

    return ctx;
};

export const useOptionalUser = (): JWTUser | null => {
    return useContext(JwtContext)?.user ?? null;
};

export default JWTProvider;
